import {
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
} from 'app/frontend/types/custom/RaidPlan'
import {
  EncounterAbilityCategory,
  EncounterAbilitySegment,
} from 'app/frontend/types/custom/EncounterEvents'
import { getEncounterAbilityAssignmentGroups } from 'app/frontend/types/custom/EncounterAbilityAssignmentDefaults'

type EncounterEntry = {
  id?: string
  time: number
  duration: number
  phaseIndex: number
  segments?: EncounterAbilitySegment[] | null
}

type EncounterAbility = {
  spell_id: number
  name?: string | null
  color: string
  entries: EncounterEntry[]
  category?: EncounterAbilityCategory | null
  categories?: EncounterAbilityCategory[]
}

type EncounterDifficultyLike = {
  abilities: Record<string, EncounterAbility>
}

export const CUSTOM_ABILITY_SPELL_ID = 136243

export type EncounterAssignmentRow = {
  abilityKey: RaidPlanEncounterAssignmentAbilityKey
  spellId: number
  name: string
  color: string
  categories?: EncounterAbilityCategory[]
  isCustom: boolean
  entries: {
    id: string
    time: number
    duration: number
    castKey: string
    phaseIndex: number
    segments?: EncounterAbilitySegment[] | null
  }[]
  assignmentGroups: number
  first: number
}

type ResolveEncounterAssignmentRowsOptions = {
  encounterDifficulty: EncounterDifficultyLike
  defaultPhaseStarts: number[]
  effectivePhaseDurations: number[]
  phaseStarts: number[]
  phaseEnds: number[]
  unknownAbilityLabel: string
  customAbilityLabel?: string
  abilityOverridesByKey?: Record<
    RaidPlanEncounterAssignmentAbilityKey,
    RaidPlanEncounterAssignmentAbility
  >
}

const toOverrideTime = (
  value: unknown,
  phaseStart: number,
  phaseEnd: number,
) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  const maxTime = Math.max(phaseStart, Math.ceil(phaseEnd) - 1)
  return Math.floor(Math.max(Math.min(value, maxTime), phaseStart))
}

const resolveAbilityCategories = (
  ability: EncounterAbility,
): EncounterAbilityCategory[] => {
  if (ability.categories && ability.categories.length > 0) {
    return ability.categories
  }
  return ability.category ? [ability.category] : []
}

const buildStandardAssignmentRow = ({
  ability,
  abilityKey,
  abilityOverride,
  defaultPhaseStarts,
  effectivePhaseDurations,
  phaseStarts,
  phaseEnds,
  unknownAbilityLabel,
  resolveCustomEntries,
}: {
  ability: EncounterAbility
  abilityKey: RaidPlanEncounterAssignmentAbilityKey
  abilityOverride: NonNullable<
    RaidPlanEncounterAssignmentAbility['cast_overrides']
  >
  defaultPhaseStarts: number[]
  effectivePhaseDurations: number[]
  phaseStarts: number[]
  phaseEnds: number[]
  unknownAbilityLabel: string
  resolveCustomEntries: (
    castOverrides: NonNullable<
      RaidPlanEncounterAssignmentAbility['cast_overrides']
    >,
    requirePhaseDuration: boolean,
  ) => {
    id: string
    time: number
    duration: number
    phaseIndex: number
  }[]
}): EncounterAssignmentRow => {
  const defaultEntries = ability.entries.flatMap((entry) => {
    const basePhaseStart = phaseStarts[entry.phaseIndex]
    const basePhaseEnd = phaseEnds[entry.phaseIndex]
    const basePhaseDuration = effectivePhaseDurations[entry.phaseIndex]
    if (
      basePhaseStart === undefined ||
      basePhaseEnd === undefined ||
      basePhaseDuration === undefined
    ) {
      return []
    }
    if (entry.time > basePhaseDuration || entry.time < 0) return []
    const override = entry.id ? abilityOverride[entry.id] : undefined
    if (override?.ignored) return []
    const overridePhaseIndex =
      typeof override?.phase_index === 'number' &&
      Number.isInteger(override.phase_index) &&
      override.phase_index >= 0 &&
      phaseStarts[override.phase_index] !== undefined &&
      phaseEnds[override.phase_index] !== undefined
        ? override.phase_index
        : entry.phaseIndex
    const phaseStart = phaseStarts[overridePhaseIndex]
    const phaseEnd = phaseEnds[overridePhaseIndex]
    const overrideTime = toOverrideTime(override?.time, phaseStart, phaseEnd)
    const time = overrideTime ?? Math.floor(basePhaseStart + entry.time)
    const placementPhaseEnd =
      overrideTime !== undefined ? phaseEnd : basePhaseEnd
    const maxDuration = placementPhaseEnd - time
    const duration = Math.max(Math.min(entry.duration, maxDuration), 0)
    if (duration <= 0) return []
    return [
      {
        id: entry.id ?? `${ability.spell_id}:${entry.phaseIndex}:${entry.time}`,
        time,
        duration,
        phaseIndex:
          overrideTime !== undefined ? overridePhaseIndex : entry.phaseIndex,
        segments: entry.segments,
      },
    ]
  })
  const customEntries = resolveCustomEntries(abilityOverride, true)
  const entries = [...defaultEntries, ...customEntries].sort(
    (left, right) => left.time - right.time,
  )
  const originalFirst = ability.entries
    .map((entry) => {
      const phaseStart = defaultPhaseStarts[entry.phaseIndex] ?? 0
      return phaseStart + entry.time
    })
    .sort((a, b) => a - b)[0]
  return {
    abilityKey,
    spellId: ability.spell_id,
    name: ability.name || unknownAbilityLabel,
    color: ability.color,
    categories: resolveAbilityCategories(ability),
    isCustom: false,
    entries: entries.map((entry, index) => ({
      ...entry,
      castKey: entry.id || `${ability.spell_id}:${index}`,
    })),
    assignmentGroups: Math.max(
      getEncounterAbilityAssignmentGroups(ability.spell_id),
      1,
    ),
    first: originalFirst ?? entries[0]?.time ?? 0,
  }
}

export const resolveEncounterAssignmentRows = ({
  encounterDifficulty,
  defaultPhaseStarts,
  effectivePhaseDurations,
  phaseStarts,
  phaseEnds,
  unknownAbilityLabel,
  customAbilityLabel = 'Custom',
  abilityOverridesByKey,
}: ResolveEncounterAssignmentRowsOptions): EncounterAssignmentRow[] => {
  const resolveCustomEntries = (
    castOverrides: NonNullable<
      RaidPlanEncounterAssignmentAbility['cast_overrides']
    >,
    requirePhaseDuration: boolean,
  ) =>
    Object.entries(castOverrides)
      .flatMap(([castId, override]) => {
        if (!override?.custom || override.ignored) return []
        const phaseIndex = override.phase_index
        if (
          typeof phaseIndex !== 'number' ||
          !Number.isInteger(phaseIndex) ||
          phaseIndex < 0
        ) {
          return []
        }
        const phaseStart = phaseStarts[phaseIndex]
        const phaseEnd = phaseEnds[phaseIndex]
        const phaseDuration = effectivePhaseDurations[phaseIndex]
        if (
          phaseStart === undefined ||
          phaseEnd === undefined ||
          (requirePhaseDuration && phaseDuration === undefined)
        ) {
          return []
        }
        const time = toOverrideTime(override.time, phaseStart, phaseEnd)
        if (time === undefined) return []
        return [
          {
            id: castId,
            time,
            duration: 0.01,
            phaseIndex,
          },
        ]
      })
      .sort((left, right) => left.time - right.time)

  const abilityOrder = Object.keys(abilityOverridesByKey ?? {})
  const abilityOrderIndexByKey = abilityOrder.reduce<Record<string, number>>(
    (acc, abilityKey, index) => {
      acc[abilityKey] = index
      return acc
    },
    {},
  )
  const encounterAbilityBySpellId = Object.values(
    encounterDifficulty.abilities,
  ).reduce<Record<number, EncounterAbility>>((acc, ability) => {
    acc[ability.spell_id] = ability
    return acc
  }, {})
  const standardRows = Object.values(encounterDifficulty.abilities)
    .map((ability) =>
      buildStandardAssignmentRow({
        ability,
        abilityKey: ability.spell_id.toString(),
        abilityOverride:
          abilityOverridesByKey?.[ability.spell_id.toString()]
            ?.cast_overrides ?? {},
        defaultPhaseStarts,
        effectivePhaseDurations,
        phaseStarts,
        phaseEnds,
        unknownAbilityLabel,
        resolveCustomEntries,
      }),
    )
    .sort((left, right) => left.first - right.first)
    .filter((row) => row.entries.length > 0)
  const extraStandardRows = Object.entries(abilityOverridesByKey ?? {})
    .flatMap(([abilityKey, storedAbility]) => {
      if (abilityKey === storedAbility.spell_id.toString()) return []
      const encounterAbility = encounterAbilityBySpellId[storedAbility.spell_id]
      if (!encounterAbility) return []
      const row = buildStandardAssignmentRow({
        ability: encounterAbility,
        abilityKey,
        abilityOverride: storedAbility.cast_overrides ?? {},
        defaultPhaseStarts,
        effectivePhaseDurations,
        phaseStarts,
        phaseEnds,
        unknownAbilityLabel,
        resolveCustomEntries,
      })
      return row.entries.length > 0 ? [row] : []
    })
    .sort((left, right) => left.first - right.first)

  const standardSpellIds = new Set(
    Object.values(encounterDifficulty.abilities).map(
      (ability) => ability.spell_id,
    ),
  )
  const customRows = Object.entries(abilityOverridesByKey ?? {})
    .flatMap(([abilityKey, ability]) => {
      if (standardSpellIds.has(ability.spell_id)) {
        return []
      }
      const castOverrides = ability.cast_overrides ?? {}
      const entries = resolveCustomEntries(castOverrides, false).map(
        (entry) => ({
          ...entry,
          castKey: entry.id,
        }),
      )
      const first = entries[0]?.time ?? Number.MAX_SAFE_INTEGER
      return [
        {
          abilityKey,
          spellId: ability.spell_id,
          name: ability.title?.trim() || customAbilityLabel,
          color: ability.rgb ?? 'rgb(255, 255, 255)',
          categories: [],
          isCustom: ability.spell_id === CUSTOM_ABILITY_SPELL_ID,
          entries,
          assignmentGroups: getEncounterAbilityAssignmentGroups(
            ability.spell_id,
          ),
          first,
        },
      ]
    })
    .sort((left, right) => {
      const leftOrder = abilityOrderIndexByKey[left.abilityKey]
      const rightOrder = abilityOrderIndexByKey[right.abilityKey]
      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder
      }
      if (leftOrder !== undefined) return -1
      if (rightOrder !== undefined) return 1
      return left.first - right.first
    })
    .map((row) => ({
      ...row,
      assignmentGroups: Math.max(row.assignmentGroups, 1),
    }))

  return [...standardRows, ...extraStandardRows, ...customRows]
}
